import { useEffect } from 'react';
import { IXappexChatbot } from '../types/common';

const SCRIPT_SRC = ENV.EXTERNAL_CHATBOT_ENDPOINT;
const CHATBOT_ROOT_ID = 'xappex-chatbot-root';
const SCRIPT_ID = 'xappex-chatbot-script';

function loadXappexScript(): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (document.getElementById(CHATBOT_ROOT_ID)) { return resolve() }

    const container = document.createElement('div');
    container.id = CHATBOT_ROOT_ID;
    container.setAttribute('data-xappex-chatbot', ENV.EXTERNAL_CHATBOT_KEY);
    document.body.appendChild(container);

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;

    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Xappex chatbot script'));

    container.appendChild(script);
  });
}

async function waitForXappexChatbot(timeoutMs = 5_000): Promise<NonNullable<IXappexChatbot>> {
  if (window.XappexChatbot) { return window.XappexChatbot }

  await loadXappexScript();

  return new Promise<NonNullable<IXappexChatbot>>((resolve, reject) => {
    const startedAt = Date.now();

    const check = () => {
      if (window.XappexChatbot) {
        resolve(window.XappexChatbot);
        return;
      }
      if (Date.now() - startedAt > timeoutMs) {
        reject(new Error("XappexChatbot was not initialized"));
        return;
      }
      requestAnimationFrame(check);
    };
    check();
  });
}

function applyChatbotOffset() {
  const el = document.querySelector<HTMLElement>('[data-xappex-chatbot-offset]');
  const offset = el?.dataset.xappexChatbotOffset ?? "20px";

  const button = document.querySelector<HTMLElement>('.xpx-cb-launcher');
  if (button) {
    button.style.bottom = offset;
  }
}

export function useXappexChatbot(email?: string | null) {
  useEffect(() => {
    applyChatbotOffset();
    const observer = new MutationObserver(() => { applyChatbotOffset() });
    observer.observe(document.body, {
      childList: true, subtree: true, attributes: true, attributeFilter: ['data-xappex-chatbot-offset']
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function syncEmail() {
      const chatbot = await waitForXappexChatbot();
      if (!cancelled) {
        chatbot.setUserEmail(email ?? null);
      }
    }

    syncEmail().catch(console.error);
    return () => { cancelled = true };
  }, [email]);
}